// Log de entregas do barramento de eventos — Fase 8.
//
// Responde a uma pergunta só, e ela sempre chega em forma de reclamação:
// "o fluxo do n8n não disparou". Sem esta tela, responder exige abrir o banco.
//
// Por isso o desenho: a falha aparece primeiro e com o motivo à vista. Uma
// tabela onde tudo tem o mesmo peso obrigaria a pessoa a procurar o problema,
// e ela vai procurar exatamente quando está com pressa.
//
// É tela de admin do Inbox. O papel vem de `inbox_members`, e a RLS das duas
// tabelas confere de novo do lado do banco.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Clock, Loader2, Radio,
  RefreshCw, ShieldAlert, Webhook,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useInboxMembro } from '../../../src/hooks/useInboxMembro';
import type { EndpointEvento, EntregaEvento, EntregaStatus } from './tipos';
import { carregarEndpoints, carregarEntregas } from './apiEventos';
import { desdeQuando } from './formato';

const ROTULO_STATUS: Record<EntregaStatus, { texto: string; classe: string; Icone: typeof Clock }> = {
  delivered: {
    texto: 'Entregue',
    classe: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    Icone: CheckCircle2,
  },
  pending: {
    texto: 'Tentando',
    classe: 'bg-secondary/20 text-[#9A6A12] dark:text-secondary',
    Icone: Clock,
  },
  failed: {
    texto: 'Falhou',
    classe: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    Icone: AlertTriangle,
  },
};

/** O que cada evento significa, em português de gente. */
const EXPLICACAO: Record<string, string> = {
  'mensagem.recebida': 'Cliente mandou mensagem',
  'mensagem.enviada': 'Respondemos o cliente',
  'ticket.aberto': 'Conversa foi aberta',
  'ticket.resolvido': 'Conversa foi resolvida',
  'conversa.atribuida': 'Conversa ganhou responsável',
  'conexao.caiu': 'Um canal caiu',
};

function Etiqueta({ status }: { status: EntregaStatus }) {
  const r = ROTULO_STATUS[status];
  const { Icone } = r;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.classe}`}>
      <Icone size={11} />
      {r.texto}
    </span>
  );
}

export default function EventosView() {
  const { profile } = useAuth();
  const { ehMembro, ehAdmin, carregando: carregandoAcesso } = useInboxMembro(profile?.id);

  const [endpoints, setEndpoints] = useState<EndpointEvento[]>([]);
  const [entregas, setEntregas] = useState<EntregaEvento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<EntregaStatus | 'todos'>('todos');
  const [expandida, setExpandida] = useState<string | null>(null);

  const buscar = useCallback(async () => {
    setErro(null);
    try {
      const [eps, ents] = await Promise.all([
        carregarEndpoints(),
        carregarEntregas({ status: filtro }),
      ]);
      setEndpoints(eps);
      setEntregas(ents);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [filtro]);

  useEffect(() => {
    if (!ehAdmin) return;
    void buscar();
  }, [ehAdmin, buscar]);

  const nomeDoEndpoint = useMemo(() => {
    const m = new Map(endpoints.map((e) => [e.id, e.nome]));
    // Endpoint apagado depois da entrega: o log sobrevive a ele, e "removido" é
    // mais honesto que um id cru na tela.
    return (id: string) => m.get(id) ?? 'destino removido';
  }, [endpoints]);

  const falhas = entregas.filter((e) => e.status === 'failed').length;

  if (carregandoAcesso) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (!ehMembro || !ehAdmin) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
        <ShieldAlert className="mx-auto mb-3 text-gray-400" size={28} />
        <p className="font-semibold text-gray-800 dark:text-slate-100">Acesso restrito</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          O log de integrações é visível só para administradores do Omnichannel.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/inbox"
            className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary dark:text-slate-400"
          >
            <ArrowLeft size={13} /> Caixa de entrada
          </Link>
          <h1 className="flex items-center gap-2 font-poppins text-xl font-bold text-gray-800 dark:text-slate-100">
            <Webhook size={20} className="text-primary" />
            Integrações
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-slate-400">
            O que o Inbox avisou para outros sistemas, e se chegou.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void buscar()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
        >
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {erro && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          {erro}
        </div>
      )}

      {/* ─── Destinos ──────────────────────────────────────────────────── */}
      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-200">Destinos</h2>

        {endpoints.length === 0 && !carregando ? (
          <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500 dark:bg-slate-800/60 dark:text-slate-400">
            Nenhum destino cadastrado. Enquanto não houver, os eventos acontecem e
            não são enviados para lugar nenhum.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {endpoints.map((e) => (
              <div
                key={e.id}
                className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-medium text-gray-800 dark:text-slate-100">{e.nome}</p>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      e.ativo
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400'
                    }`}
                  >
                    {e.ativo ? 'Ativo' : 'Desligado'}
                  </span>
                </div>
                {/* A URL pode ter caminho secreto; mostramos só o domínio. */}
                <p className="mt-0.5 truncate text-[11px] text-gray-400">
                  {(() => {
                    try {
                      return new URL(e.url).hostname;
                    } catch {
                      return e.url;
                    }
                  })()}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {e.eventos.map((ev) => (
                    <span
                      key={ev}
                      title={EXPLICACAO[ev] ?? ev}
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-slate-700 dark:text-slate-300"
                    >
                      {EXPLICACAO[ev] ?? ev}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ─── Entregas ──────────────────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-slate-200">
            Últimos envios
            {falhas > 0 && filtro === 'todos' && (
              <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {falhas} {falhas === 1 ? 'falha' : 'falhas'}
              </span>
            )}
          </h2>

          <div className="flex gap-1">
            {(['todos', 'failed', 'pending', 'delivered'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFiltro(f)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  filtro === f
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700'
                }`}
              >
                {f === 'todos' ? 'Todos' : ROTULO_STATUS[f].texto}
              </button>
            ))}
          </div>
        </div>

        {carregando ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : entregas.length === 0 ? (
          <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500 dark:bg-slate-800/60 dark:text-slate-400">
            {filtro === 'todos'
              ? 'Nenhum envio ainda.'
              : `Nenhum envio com status "${ROTULO_STATUS[filtro as EntregaStatus].texto}".`}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
            {entregas.map((e, i) => {
              const aberta = expandida === e.id;
              return (
                <div
                  key={e.id}
                  className={`${i > 0 ? 'border-t border-gray-100 dark:border-slate-700/60' : ''} ${
                    e.status === 'failed' ? 'bg-red-50/40 dark:bg-red-900/10' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setExpandida(aberta ? null : e.id)}
                    className="flex w-full items-start gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700/40"
                  >
                    <Radio size={15} className="mt-0.5 shrink-0 text-gray-400" />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-gray-800 dark:text-slate-100">
                          {EXPLICACAO[e.evento] ?? e.evento}
                        </span>
                        <Etiqueta status={e.status} />
                        {e.tentativas > 1 && (
                          <span className="text-[11px] text-gray-400">
                            {e.tentativas} tentativas
                          </span>
                        )}
                      </div>

                      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-slate-400">
                        {nomeDoEndpoint(e.endpoint_id)} · {desdeQuando(e.updated_at)}
                        {e.response_code ? ` · respondeu ${e.response_code}` : ''}
                      </p>

                      {/* O motivo fica visível SEM precisar abrir: é a única coisa
                          que a pessoa veio buscar quando algo falhou. */}
                      {e.status === 'failed' && e.failure_reason && (
                        <p className="mt-1 line-clamp-2 rounded bg-red-100/60 px-2 py-1 text-[11px] text-red-800 dark:bg-red-900/20 dark:text-red-300">
                          {e.failure_reason}
                        </p>
                      )}
                    </div>
                  </button>

                  {aberta && (
                    <div className="border-t border-gray-100 bg-gray-50/70 px-3 py-2.5 dark:border-slate-700/60 dark:bg-slate-900/40">
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        O que foi enviado
                      </p>
                      {/* Só identificadores: o evento nunca carrega texto de
                          mensagem, telefone ou e-mail. Ver `eventosInbox.ts`. */}
                      <pre className="max-h-56 overflow-auto rounded bg-white p-2 text-[11px] leading-relaxed text-gray-700 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                        {JSON.stringify(e.payload ?? {}, null, 2)}
                      </pre>
                      {e.status === 'failed' && (
                        <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">
                          Já foram feitas todas as tentativas. Corrija o destino e
                          o próximo evento será enviado normalmente — este não é
                          reenviado sozinho.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 text-[11px] text-gray-400">
          O registro guarda os últimos 90 dias. Nenhum evento carrega texto de
          mensagem, telefone ou e-mail — só identificadores.
        </p>
      </section>
    </div>
  );
}
